import React from 'react';
import { createRouter, createRoute, createRootRoute, Outlet, redirect, useSearch, useParams } from '@tanstack/react-router';
import { Layout } from './components/layout/Layout';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { InventoryPage } from './features/inventory/pages/InventoryPage';
import { TradesPage } from './features/trades/pages/TradesPage';
import { TradeDetailPage } from './features/trades/pages/TradeDetailPage';
import { CounterpartiesPage } from './features/counterparties/pages/CounterpartiesPage';
import { CollateralManagerPage } from './features/collateral/pages/CollateralManagerPage';
import { BillingPage } from './features/billing/pages/BillingPage';
import { BillingDocumentPage } from './features/billing/pages/BillingDocumentPage';
import { LoginPage } from './features/auth/pages/LoginPage';
import { AuthGuard } from './components/auth';
import { useLocation } from '@tanstack/react-router';
import { mergeInventorySearch } from './features/inventory/router-helpers';
// Search-param coercion lives in a standalone, unit-tested module (the two
// validateSearch blocks stay in lock-step and the producer logic is directly
// testable without importing router.tsx; LR-478 review r2/r6).
import {
  parseCp,
  parseCounterpartyTab,
  parseHview,
  parseViewId,
  parseSel,
  parseTradeDetailSearch,
} from './features/trades/trade-detail-search';
import { parseBillingSearch } from './features/billing/billing-search';
import { setUnauthorizedHandler } from './services/api/unauthorized-handler';

// eslint-disable-next-line react-refresh/only-export-components -- router config file: RootComponent is colocated with the router instance export by design
function RootComponent() {
  const location = useLocation();

  if (location.pathname === '/login') {
    return (
      <AuthGuard requireAuth={false}>
        <Outlet />
      </AuthGuard>
    );
  }

  return (
    <AuthGuard requireAuth={true}>
      <Layout>
        <Outlet />
      </Layout>
    </AuthGuard>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/inventory', search: mergeInventorySearch(undefined, {}) });
  },
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory',
  component: InventoryPage,
  validateSearch: (search: Record<string, unknown>) =>
    mergeInventorySearch(undefined, {
      tab: search.tab as string | undefined,
      cp: search.cp as string | undefined,
      reportTab: search.reportTab as string | undefined,
      view_id: search.view_id ? Number(search.view_id) : undefined,
    }),
});

// eslint-disable-next-line react-refresh/only-export-components -- router config file: TradesWithBoundary is colocated with the route definition by design
function TradesWithBoundary(): React.ReactElement {
  const { mode } = useSearch({ from: '/trades' });
  // LR-376: History mode shares the TradesPage with mode='trades' — the page
  // reads the search param itself and applies the terminal-status filter.
  // LR-587 moved Mark to Market out to /collateral, so this route is Trades and
  // History only; the boundary stays (LR-104).
  //
  // kvi-r1 #3: `key={mode}` remounts the boundary when the operator toggles
  // between Trades and History. Without it, a crash in one leaves the shared
  // boundary in hasError=true and blocks the other until a full page reload.
  return (
    <ErrorBoundary key={mode ?? 'trades'}>
      <TradesPage />
    </ErrorBoundary>
  );
}

// Exported so the mode=mtm redirect and the search parse can be exercised against
// the route the app actually mounts (same reason as `billingRoute` below): the page
// tests mock @tanstack/react-router wholesale, so nothing else fails if either line
// is deleted.
export const tradesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trades',
  component: TradesWithBoundary,
  // LR-587: /trades?mode=mtm was the Mark to Market tab. It now lives in
  // Collateral Manager, so old links, bookmarks and the release notes' own deep
  // links land there instead of on an empty Trades blotter. `mode` still parses
  // 'mtm' below purely so this redirect can see it.
  beforeLoad: ({ search }) => {
    if (search.mode === 'mtm') {
      throw redirect({
        to: '/collateral',
        search: { product: 'gmsla' as const, leg: 'fee' as const, cp: search.cp },
      });
    }
  },
  validateSearch: (search: Record<string, unknown>): {
    counterparty?: number | string;
    view_id?: number;
    mode: 'mtm' | 'history' | undefined;
    cp: number | undefined;
    htab?: 'mtm' | 'trades' | 'cashpool';
    hview?: 'date' | 'cp';
    sel?: string;
  } => ({
    // The counterparty TAB is `?counterparty=<id>` (LR-671 renamed it from
    // `?view=`); `?view_id=` is the saved view — same name as /inventory, which
    // the shared saved-views module and Copy Link rely on. A slug (pre-id link)
    // and the old `?view=` are accepted as aliases for one release (bookmarks,
    // links pasted in chats) and rewritten on the next URL write by
    // use-url-view-sync; a slug resolves only against the tabs the browser has,
    // so one past the first page of the tab list falls back to All Trades.
    // `cp` stays the numeric MTM counterparty id.
    counterparty: parseCounterpartyTab(search.counterparty ?? search.view),
    view_id: parseViewId(search.view_id),
    mode: search.mode === 'mtm'
      ? ('mtm' as const)
      : search.mode === 'history'
        ? ('history' as const)
        : undefined,
    cp: parseCp(search.cp),
    // LR-390: History subtab (Trades | Mark to Market). Only meaningful when mode=history.
    // LR-479: 'cashpool' is a sibling subtab for the repo cash-pool posting log.
    htab: search.htab === 'mtm'
      ? ('mtm' as const)
      : search.htab === 'trades'
        ? ('trades' as const)
        : search.htab === 'cashpool'
          ? ('cashpool' as const)
          : undefined,
    // LR-498: History master–detail left-rail view + deep-link selection. Only
    // meaningful when mode=history & htab is mtm/cashpool; HistoryShell applies the
    // per-tab default ('date' for mtm, 'cp' for cashpool) when hview is absent.
    hview: parseHview(search.hview),
    sel: parseSel(search.sel),
  }),
});

// eslint-disable-next-line react-refresh/only-export-components -- router config file: TradeDetailWithBoundary is colocated with the route definition by design
function TradeDetailWithBoundary(): React.ReactElement {
  // LR-478: route-level crash containment, mirroring TradesOrMtm on /trades.
  // The single-trade page now carries non-trivial render (origin-aware
  // breadcrumb, roving toolbar, counterparty fetch); without a boundary a throw
  // there blanks the whole app shell. `key={tradeId}` remounts the boundary when
  // the operator opens a different trade, so a crash on one trade doesn't poison
  // the next (same rationale as kvi-r1 #3 on the Trades/MTM boundary).
  const { tradeId } = useParams({ from: '/trades/$tradeId' });
  return (
    <ErrorBoundary key={tradeId}>
      <TradeDetailPage />
    </ErrorBoundary>
  );
}

const tradeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trades/$tradeId',
  component: TradeDetailWithBoundary,
  // LR-478: origin-aware breadcrumb signal. `from` records which surface opened
  // the trade (blotter / MTM / History) so the breadcrumb root + its return
  // target match where the operator came from; `cp` carries the MTM counterparty
  // scope. Both are written onto the trade-ID <Link>'s search (refresh-safe,
  // unlike a store that loses origin on hard reload). Unknown values are stripped
  // to undefined → the breadcrumb falls back to "Trades".
  validateSearch: (search: Record<string, unknown>) => parseTradeDetailSearch(search),
});

const counterpartiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/counterparties',
  component: CounterpartiesPage,
});

// LR-580: Collateral Manager section; LR-587 moved the Mark to Market content in
// here and retired that tab, then restructured it to the product's 12.08 spec:
// level 1 is the agreement (GMRA | GMSLA), level 2 the collateral leg (GMRA →
// Cash Pool; GMSLA → Cash Pool Fee | Cash Rebate — Non-Cash and Uncollateralized
// are product-deferred and carry no tab). Product, leg and the counterparty
// scope live in the URL so a view is shareable and survives F5 — the same
// contract /trades?mode=mtm&cp= used to offer. An invalid leg for the product
// normalises rather than 404s, so an old or hand-edited link still lands.
export const collateralRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collateral',
  component: CollateralManagerPage,
  validateSearch: (search: Record<string, unknown>): {
    product: 'gmra' | 'gmsla';
    leg: 'cash' | 'fee' | 'rebate';
    cp: number | undefined;
  } => {
    // Legacy pre-restructure links: ?tab=gmsla-mtm was the GMSLA marking
    // workspace; ?tab=cash-pools showed the register + the GMRA call view.
    const legacyGmsla = search.tab === 'gmsla-mtm';
    const product =
      search.product === 'gmsla' || legacyGmsla
        ? ('gmsla' as const)
        : ('gmra' as const);
    const leg =
      product === 'gmra'
        ? ('cash' as const)
        : search.leg === 'rebate'
          ? ('rebate' as const)
          : ('fee' as const);
    return { product, leg, cp: parseCp(search.cp) };
  },
});

// Exported so the search round trip can be tested against the route the app
// actually mounts — a test that rebuilds its own route would still pass if the
// validateSearch line below were deleted.
export const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  component: BillingPage,
  // LR-551: the statements-list filters live in the URL so a filtered list is
  // shareable and survives F5. Coercion (and dropping out-of-range values) is in
  // the unit-tested billing-search module, like the trade routes.
  validateSearch: (search: Record<string, unknown>) => parseBillingSearch(search),
});

// eslint-disable-next-line react-refresh/only-export-components -- router config file: BillingDocumentWithBoundary is colocated with the route definition by design
function BillingDocumentWithBoundary(): React.ReactElement {
  // LR-557: route-level crash containment, mirroring the trade-detail route.
  // `key={statementId}` remounts the boundary when another document opens, so a
  // crash on one statement doesn't poison the next.
  const { statementId } = useParams({ from: '/billing/$statementId' });
  return (
    <ErrorBoundary key={statementId}>
      <BillingDocumentPage />
    </ErrorBoundary>
  );
}

// LR-557: the counterparty's period document, addressed by any member statement
// id (the backend serves the whole counterparty+month document for it).
const billingDocumentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/$statementId',
  component: BillingDocumentWithBoundary,
});

const recallReportRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recall-report',
  beforeLoad: () => {
    throw redirect({
      to: '/inventory',
      search: mergeInventorySearch(undefined, { tab: 'counterparty-reports' }),
    });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  inventoryRoute,
  tradesRoute,
  tradeDetailRoute,
  counterpartiesRoute,
  collateralRoute,
  billingRoute,
  billingDocumentRoute,
  recallReportRedirectRoute,
  loginRoute,
]);

export const router = createRouter({
  routeTree,
  context: {
    cache: new Map(),
  },
});

setUnauthorizedHandler(() => router.navigate({ to: '/login' }));

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
